import { useId, useState } from "react";
import {
  CONDITION_LABELS,
  flattenLocations,
  formatMoney,
  ITEM_CONDITIONS,
  ITEM_STATUSES,
  MAX_QUANTITY,
  normaliseQuantity,
  pathLabel,
  SERIAL_FIELD_LABEL,
  STATUS_LABELS,
  type ItemCondition,
  type ItemStatus,
  type ItemWriteRequest,
} from "@iltocat/shared";
import { centsFromInput, priceFromCents, priceWithMarkup } from "@iltocat/shared";
import { hasCamera } from "./CameraView.js";
import { ScanSerialDialog } from "./ScanSerialDialog.js";
import { Field } from "./Field.js";
import { Icon } from "./Icon.js";
import { Panel } from "./Panel.js";
import { FieldsEditor, blankField, fieldsToRequest, type FieldDraft } from "./FieldsEditor.js";
import { Picker, type Option } from "./Picker.js";
import { TagInput } from "./TagInput.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import type { Vocabulary } from "../lib/useVocabulary.js";

/**
 * Everything about an item that is words rather than pictures, in the order
 * somebody actually fills it in.
 *
 * The same form serves capture and editing, because they are the same fields
 * and a second copy would drift. What differs is how much is showing: during a
 * capture, the title, location and category are on screen and everything else
 * is behind a disclosure, because every extra control is a decision somebody
 * has to skip fifty times in a sitting. On the item screen, where the point is
 * to finish something, it is all open, as four cards grouped by what the
 * fields are for.
 *
 * **Price and condition are built in** by the owner's locked decision, and they
 * are here in Stage 1 rather than in Stage 4 so that the listing export is a
 * screen rather than a migration. They live under the disclosure: they are not
 * what capture is for.
 *
 * **In a stockroom the serial number is built in too**, as a box under the
 * title. It is still stored as the "Serial number" field, which is what search,
 * labels and the stock lists read, so this is only where it is edited: the
 * Fields list leaves that one field out rather than showing it twice. The
 * camera button beside it reads the serial off the box (`ScanSerialDialog`).
 */

const isSerial = (field: FieldDraft): boolean =>
  field.label.trim().toLowerCase() === SERIAL_FIELD_LABEL.toLowerCase();

export interface Draft {
  title: string;
  description: string;
  notes: string;
  locationId: string | null;
  categoryId: string | null;
  /** As typed, not as cents: half a price is a normal state for a text box to be in. */
  price: string;
  condition: ItemCondition;
  status: ItemStatus;
  /** As typed, for the same reason the price is. */
  quantity: string;
  tags: string[];
  fields: FieldDraft[];
}

/**
 * What a stock item cost, so its price can be read against it. Private, like
 * the cost itself: it is shown to the inventory's own member on the item
 * screen and is never part of the draft, so it cannot be saved as a field.
 */
export interface CostHint {
  costCents: number;
  /** The inventory's standard markup, whole percent. */
  markup: number;
}

/** The price's tie to the unit cost: the markup it works out to, and a way to set it from the standard one. */
function PriceLink({
  hint,
  price,
  onPrice,
}: {
  hint: CostHint;
  price: string;
  onPrice: (price: string) => void;
}) {
  const cents = centsFromInput(price);
  const suggested = priceWithMarkup(hint.costCents, hint.markup) ?? 0;
  const markup =
    cents !== null && hint.costCents > 0
      ? Math.round(((cents - hint.costCents) / hint.costCents) * 100)
      : null;
  return (
    <p className="price-link muted small">
      <Icon name="lock" size={12} /> Unit cost {formatMoney(hint.costCents)}
      {cents !== null && (
        <>
          , {formatMoney(Math.abs(cents - hint.costCents))}{" "}
          {cents >= hint.costCents ? "margin" : "below cost"}
          {markup !== null && ` (${markup}% markup)`}
        </>
      )}
      {cents !== suggested && (
        <button type="button" className="link" onClick={() => onPrice(priceFromCents(suggested))}>
          Set to cost + {hint.markup}%
        </button>
      )}
    </p>
  );
}

export const emptyDraft = (): Draft => ({
  title: "",
  description: "",
  notes: "",
  locationId: null,
  categoryId: null,
  price: "",
  condition: "",
  status: "keep",
  quantity: "1",
  tags: [],
  fields: [],
});

/** What the worker is sent. The price becomes cents here, at the edge, and nowhere else. */
export const draftToRequest = (draft: Draft): ItemWriteRequest => ({
  title: draft.title,
  description: draft.description,
  notes: draft.notes,
  locationId: draft.locationId,
  categoryId: draft.categoryId,
  priceCents: centsFromInput(draft.price),
  condition: draft.condition,
  status: draft.status,
  quantity: normaliseQuantity(draft.quantity),
  tags: draft.tags,
  fields: fieldsToRequest(draft.fields),
});

export function ItemForm({
  draft,
  onChange,
  taxonomy,
  vocabulary,
  expanded = false,
  costHint,
  serial = false,
}: {
  /** In a stock inventory, on an item that is on the shelf: what it cost. */
  costHint?: CostHint | null | undefined;
  /** In a stock inventory: the serial number is a box of its own, not a field to add. */
  serial?: boolean;
  draft: Draft;
  onChange: (draft: Draft) => void;
  taxonomy: Taxonomy;
  /** Tags and modular fields need this. Capture leaves it out: they are for finishing, not for speed. */
  vocabulary?: Vocabulary;
  /** True on the item screen, where finishing is the point. False during a capture. */
  expanded?: boolean;
}) {
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => onChange({ ...draft, [key]: value });
  const [scanning, setScanning] = useState(false);
  const serialId = useId();

  // A location is offered by its whole path ("Basement › Shelf A › Bin 3"),
  // because "Bin 3" on its own is not an answer to "where is it".
  const locationOptions: Option[] = flattenLocations(taxonomy.tree).map((node) => ({
    id: node.id,
    label: pathLabel(node.path),
  }));
  const labelOf = (id: string): string => locationOptions.find((option) => option.id === id)?.label ?? "";

  const categoryOptions: Option[] = taxonomy.categories.map((category) => ({
    id: category.id,
    label: category.name,
  }));

  const lastLocation = taxonomy.recentLocations[0];

  // The first serial field is the one that counts (see `SERIAL_SQL` in the
  // object); any later one is an ordinary field and stays in the list.
  const serialField = serial ? draft.fields.find(isSerial) : undefined;
  const otherFields = serialField ? draft.fields.filter((field) => field !== serialField) : draft.fields;
  const setSerial = (value: string) =>
    set(
      "fields",
      serialField
        ? draft.fields.map((field) => (field === serialField ? { ...field, value } : field))
        : [blankField({ label: SERIAL_FIELD_LABEL, value }), ...draft.fields],
    );

  const basics = (
    <>
      <Field
        label="Title"
        value={draft.title}
        onChange={(value) => set("title", value)}
        placeholder={expanded ? "What is it?" : "Optional"}
      />

      {serial && (
        <div className="field">
          <label className="field-label" htmlFor={serialId}>
            {SERIAL_FIELD_LABEL}
          </label>
          <div className="input-action">
            <input
              id={serialId}
              className="input mono"
              value={serialField?.value ?? ""}
              placeholder="Scan or type it"
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => setSerial(event.target.value)}
            />
            {hasCamera() && (
              <button
                type="button"
                className="icon-button icon-button-bordered"
                aria-label="Scan the serial number with the camera"
                title="Scan with the camera"
                onClick={() => setScanning(true)}
              >
                <Icon name="camera" size={20} />
              </button>
            )}
          </div>
          {scanning && <ScanSerialDialog onUse={setSerial} onClose={() => setScanning(false)} />}
        </div>
      )}

      <Picker
        label="Location"
        icon="pin"
        value={draft.locationId}
        options={locationOptions}
        recents={taxonomy.recentLocations.map((location) => ({
          id: location.id,
          label: labelOf(location.id),
        }))}
        onChange={(id) => set("locationId", id)}
        onCreate={async (name, inside) => (await taxonomy.addLocation(name, inside))?.id ?? null}
        placeholder="Basement, Shelf A, Bin 3…"
        inside={lastLocation ? { id: lastLocation.id, label: labelOf(lastLocation.id) } : null}
      />

      <Picker
        label="Category"
        icon="folder"
        value={draft.categoryId}
        options={categoryOptions}
        recents={taxonomy.recentCategories.map((category) => ({
          id: category.id,
          label: category.name,
        }))}
        onChange={(id) => set("categoryId", id)}
        onCreate={async (name) => (await taxonomy.addCategory(name))?.id ?? null}
        placeholder="Tools, Books, Kitchen…"
      />
    </>
  );

  const words = (
    <>
      <label className="field">
        <span className="field-label">Description</span>
        <textarea
          className="input input-area"
          rows={3}
          value={draft.description}
          onChange={(event) => set("description", event.target.value)}
        />
      </label>

      <label className="field">
        <span className="field-label">
          <Icon name="lock" size={13} />
          Private notes
        </span>
        <textarea
          className="input input-area"
          rows={2}
          placeholder="Never shared in a listing or a prompt"
          value={draft.notes}
          onChange={(event) => set("notes", event.target.value)}
        />
      </label>
    </>
  );

  const status = (
    <label className="field">
      <span className="field-label">Status</span>
      <select
        className="input"
        value={draft.status}
        onChange={(event) => set("status", event.target.value as ItemStatus)}
      >
        {ITEM_STATUSES.map((status) => (
          <option key={status} value={status}>
            {STATUS_LABELS[status]}
          </option>
        ))}
      </select>
    </label>
  );

  const quantity = (
    <label className="field">
      <span className="field-label">Quantity</span>
      <input
        className="input"
        type="number"
        min={1}
        max={MAX_QUANTITY}
        inputMode="numeric"
        value={draft.quantity}
        onChange={(event) => set("quantity", event.target.value)}
      />
    </label>
  );

  const selling = (
    <>
      <Field
        label="Price"
        value={draft.price}
        onChange={(value) => set("price", value)}
        placeholder="45.00"
        type="text"
      />

      <label className="field">
        <span className="field-label">Condition</span>
        <select
          className="input"
          value={draft.condition}
          onChange={(event) => set("condition", event.target.value as ItemCondition)}
        >
          {ITEM_CONDITIONS.map((condition) => (
            <option key={condition} value={condition}>
              {CONDITION_LABELS[condition]}
            </option>
          ))}
        </select>
      </label>
    </>
  );

  if (!expanded) {
    return (
      <div className="stack">
        {basics}
        <details className="more">
          <summary>
            <Icon name="chevron-right" size={16} />
            More details
          </summary>
          <div className="stack">
            {words}
            <div className="split">
              {selling}
              {status}
              {quantity}
            </div>
          </div>
        </details>
      </div>
    );
  }

  return (
    <>
      <Panel title="Basics">
        <div className="stack">{basics}</div>
      </Panel>

      <Panel title="Description" collapsible>
        <div className="stack">{words}</div>
      </Panel>

      <Panel title="Status and price" collapsible>
        <div className="split">
          {status}
          {quantity}
          {selling}
        </div>
        {costHint && (
          <PriceLink hint={costHint} price={draft.price} onPrice={(price) => set("price", price)} />
        )}
      </Panel>

      {vocabulary && (
        <Panel title="Tags and fields" collapsible>
          <div className="stack">
            <TagInput
              tags={draft.tags}
              onChange={(tags) => set("tags", tags)}
              known={vocabulary.tags}
              reserve={vocabulary.reserveTags}
            />
            <FieldsEditor
              fields={otherFields}
              onChange={(fields) => set("fields", serialField ? [serialField, ...fields] : fields)}
              vocabulary={vocabulary}
              builtIn={serial ? [SERIAL_FIELD_LABEL] : []}
            />
          </div>
        </Panel>
      )}
    </>
  );
}
